import { getConfig } from '@/config/runtime'

// Request/Response types based on PRD specs
export interface PinConversationRequest {
  conversationId: string
  title: string
  creationTime: string
  email: string
  applicationId: string
  accessKeyId?: string
  secretAccessKey?: string
  sessionToken?: string
}

export interface UnpinConversationRequest {
  conversationId: string
  email: string
  applicationId: string
  accessKeyId?: string
  secretAccessKey?: string
  sessionToken?: string
}

export interface PinConversationResponse {
  success: boolean
  message: string
  conversationId: string
  email: string
  applicationId: string
  startTime: string
  title: string
}

export interface UnpinConversationResponse {
  success: boolean
  message: string
  conversationId: string
  creationTime: string
  email: string
  applicationId: string
}

// Service function to pin a conversation
export const pinConversation = async (
  request: PinConversationRequest
): Promise<PinConversationResponse> => {
  const API_ENDPOINT = `${getConfig().plus.apiUrl}/pin-conversation`

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      let errorMessage = `Pin conversation failed with status ${response.status}`
      try {
        const errorData = await response.json()
        errorMessage = errorData.message || JSON.stringify(errorData) || errorMessage
      } catch (e) {
        console.error('Error parsing pin conversation error response:', e)
      }
      throw new Error(errorMessage)
    }

    const data = await response.json() as PinConversationResponse

    if (!data.success) {
      throw new Error(data.message || 'Pin conversation failed')
    }

    return data
  } catch (error) {
    console.error('Error in pinConversation service:', error)
    throw error
  }
}

// Service function to unpin a conversation
export const unpinConversation = async (
  request: UnpinConversationRequest
): Promise<UnpinConversationResponse> => {
  const API_ENDPOINT = `${getConfig().plus.apiUrl}/unpin-conversation`

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      let errorMessage = `Unpin conversation failed with status ${response.status}`
      try {
        const errorData = await response.json()
        errorMessage = errorData.message || JSON.stringify(errorData) || errorMessage
      } catch (e) {
        console.error('Error parsing unpin conversation error response:', e)
      }
      throw new Error(errorMessage)
    }

    const data = await response.json() as UnpinConversationResponse

    if (!data.success) {
      throw new Error(data.message || 'Unpin conversation failed')
    }

    return data
  } catch (error) {
    console.error('Error in unpinConversation service:', error)
    throw error
  }
}